// src/config/themes.ts
import { createTheme, ThemeOptions } from '@mui/material/styles';

// Base color palettes for different themes
export const colorPalettes = {
  dark: {
    background: {
      primary: '#0d1117',      // Very dark gray/black
      secondary: '#161b22',    // Dark gray cards
      tertiary: '#21262d',     // Lighter gray surfaces
      paper: '#0d1117',        // Paper background
    },
    text: {
      primary: '#f0f6fc',      // Pure white text
      secondary: '#8b949e',    // Gray text
      disabled: '#484f58',     // Disabled text
    },
    accent: {
      fire: '#ff6b35',         // Fire orange
      fireSecondary: '#ff8c42', // Light fire orange
      fireLight: '#ffab73',    // Very light fire
      fireDark: '#cc4b1c',     // Dark fire
    },
    border: '#30363d',         // Border color
    divider: '#21262d',        // Divider color
  },
  light: {
    background: {
      primary: '#ffffff',
      secondary: '#f6f8fa',
      tertiary: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#24292f',
      secondary: '#656d76',
      disabled: '#8c959f',
    },
    accent: {
      fire: '#ff6b35',
      fireSecondary: '#ff8c42',
      fireLight: '#ffab73',
      fireDark: '#cc4b1c',
    },
    border: '#d0d7de',
    divider: '#d8dee4',
  },
  midnight: {
    background: {
      primary: '#0a0a0a',
      secondary: '#1a1a1a',
      tertiary: '#2a2a2a',
      paper: '#0a0a0a',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b3b3b3',
      disabled: '#666666',
    },
    accent: {
      fire: '#ff4444',
      fireSecondary: '#ff6666',
      fireLight: '#ff8888',
      fireDark: '#cc2222',
    },
    border: '#333333',
    divider: '#222222',
  },
  ocean: {
    background: {
      primary: '#0f1419',
      secondary: '#1f2937',
      tertiary: '#374151',
      paper: '#0f1419',
    },
    text: {
      primary: '#f9fafb',
      secondary: '#9ca3af',
      disabled: '#6b7280',
    },
    accent: {
      fire: '#f59e0b',
      fireSecondary: '#fbbf24',
      fireLight: '#fcd34d',
      fireDark: '#d97706',
    },
    border: '#4b5563',
    divider: '#374151',
  },
};

// Logo colors (consistent across themes)
export const logoColors = {
  teal: '#00bca8',
  orange: '#ffc233',
  darkGray: '#3c4043',
};

// Theme creator function
export const createLoxiaTheme = (themeName: keyof typeof colorPalettes): any => {
  const palette = colorPalettes[themeName];
  
  const themeOptions: ThemeOptions = {
    palette: {
      mode: themeName === 'light' ? 'light' : 'dark',
      primary: {
        main: palette.accent.fire,
        light: palette.accent.fireLight,
        dark: palette.accent.fireDark,
        contrastText: palette.text.primary,
      },
      secondary: {
        main: palette.accent.fireSecondary,
        light: palette.accent.fireLight,
        dark: palette.accent.fire,
        contrastText: palette.text.primary,
      },
      background: {
        default: palette.background.primary,
        paper: palette.background.secondary,
      },
      text: {
        primary: palette.text.primary,
        secondary: palette.text.secondary,
        disabled: palette.text.disabled,
      },
      divider: palette.divider,
      success: {
        main: '#22c55e',
        light: '#4ade80',
        dark: '#16a34a',
      },
      warning: {
        main: palette.accent.fireSecondary,
        light: palette.accent.fireLight,
        dark: palette.accent.fire,
      },
      error: {
        main: '#ef4444',
        light: '#f87171',
        dark: '#dc2626',
      },
      info: {
        main: logoColors.teal,
        light: '#34d399',
        dark: '#059669',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "SF Pro Display", system-ui, sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.2,
        color: palette.text.primary,
      },
      h2: {
        fontWeight: 600,
        fontSize: '2rem',
        lineHeight: 1.3,
        color: palette.text.primary,
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
        lineHeight: 1.4,
        color: palette.text.primary,
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
        color: palette.text.primary,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.5,
        color: palette.text.primary,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.5,
        color: palette.text.primary,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 20px',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${palette.accent.fire}40`,
            },
          },
          contained: {
            backgroundColor: palette.accent.fire,
            color: palette.text.primary,
            '&:hover': {
              backgroundColor: palette.accent.fireDark,
            },
          },
          outlined: {
            borderColor: palette.accent.fire,
            color: palette.accent.fire,
            '&:hover': {
              borderColor: palette.accent.fireDark,
              backgroundColor: `${palette.accent.fire}10`,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.secondary,
            border: `1px solid ${palette.border}`,
            color: palette.text.primary,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 25px ${palette.background.tertiary}80`,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.secondary,
            backgroundImage: 'none',
            color: palette.text.primary,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.secondary,
            borderBottom: `1px solid ${palette.border}`,
            boxShadow: 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.border,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.accent.fire,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.accent.fire,
              },
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.tertiary,
            '& .MuiTableCell-head': {
              color: palette.text.primary,
              fontWeight: 600,
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.tertiary,
          },
          bar: {
            backgroundColor: palette.accent.fire,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
          colorPrimary: {
            backgroundColor: palette.accent.fire,
            color: palette.text.primary,
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

// Theme names for UI selector
export const themeNames = {
  dark: 'Dark',
  light: 'Light',
  midnight: 'Midnight',
  ocean: 'Ocean',
} as const;

export type ThemeName = keyof typeof themeNames;
